import { useApiScope } from '@commenda/api/scope';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';
import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { extractCommendaOSFields } from '../utils/commenda-os-extractors';
import { CommendaOSReadHelper } from '../utils/commenda-os-read-helper';
import { EntryCompletenessHelper } from '../utils/entry-completeness-helper';
import type {
  EntryCompletenessStatus,
  EntryCompletenessTarget,
  LinkedFormCompletenessSource,
} from '../utils/entry-completeness.types';

interface UseEntryCompletenessParams {
  /** The linked form to check against, or null when the picker has none. */
  linkedForm: LinkedFormCompletenessSource | null;
  targets: readonly EntryCompletenessTarget[];
}

/**
 * The status of each selected entry against the linked form. A complete entry has no status;
 * one still loading is `checking`, and one whose form or saved answers could not be read is
 * `unchecked`, so a failed read is never reported as a missing field.
 */
export const useEntryCompleteness = ({
  linkedForm,
  targets,
}: UseEntryCompletenessParams) => {
  const { shareToken } = useApiScope();
  const linkedFormDsl = linkedForm?.dsl ?? null;
  const fieldMappings = useMemo(
    () => (linkedFormDsl ? extractCommendaOSFields(linkedFormDsl) : []),
    [linkedFormDsl]
  );
  const savedTargets = useMemo(
    () => targets.filter((target) => target.readContext?.companyId),
    [targets]
  );

  const swrKey =
    linkedFormDsl && savedTargets.length > 0 && fieldMappings.length > 0
      ? JSON.stringify({
          url: 'entry-completeness',
          fields: fieldMappings.map((mapping) => mapping.fieldId),
          contexts: savedTargets.map((target) => target.readContext),
          shareToken,
        })
      : null;

  const {
    data: savedValuesByEntryId,
    error: savedValuesError,
    mutate,
  } = useSWR<Record<string, Record<string, unknown>>>(
    swrKey,
    async () => {
      const entries = await Promise.all(
        savedTargets.map(
          async (target) =>
            [
              target.entryId,
              await CommendaOSReadHelper.readFormValues(
                fieldMappings,
                // Filtered above to targets with a context.
                target.readContext as DSLContext,
                shareToken
              ),
            ] as const
        )
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false }
  );

  const statusByEntryId = useMemo(() => {
    const statuses: Record<string, EntryCompletenessStatus> = {};
    if (!linkedForm) return statuses;
    targets.forEach((target) => {
      if (linkedForm.isLoading) {
        statuses[target.entryId] = { kind: 'checking' };
        return;
      }
      if (linkedForm.hasFailed) {
        statuses[target.entryId] = { kind: 'unchecked' };
        return;
      }
      if (!linkedForm.dsl) return;
      const needsSavedValues = !!target.readContext && fieldMappings.length > 0;
      const savedValues = savedValuesByEntryId?.[target.entryId];
      if (needsSavedValues && !savedValues) {
        statuses[target.entryId] = savedValuesError
          ? { kind: 'unchecked' }
          : { kind: 'checking' };
        return;
      }
      const missingFields = EntryCompletenessHelper.findMissingFields(
        linkedForm.dsl,
        { ...savedValues, ...target.values }
      );
      if (missingFields.length > 0) {
        statuses[target.entryId] = { kind: 'incomplete', missingFields };
      }
    });
    return statuses;
  }, [
    linkedForm,
    targets,
    fieldMappings.length,
    savedValuesByEntryId,
    savedValuesError,
  ]);

  // For a host that saves a record outside the picker and must not keep the stale answers.
  const refreshSavedValues = useCallback(() => {
    mutate().catch(() => undefined);
  }, [mutate]);

  return { statusByEntryId, refreshSavedValues };
};
